import React, { useEffect, useRef, useState, useCallback } from "react";
import "./Chatting.css";
import VivahApi from "../../api";
import chatWebSocket from "../../services/chatWebSocket.service";
import authService from "../../services/auth.service";

function Chatting({ chatUser, onClose }) {
  const currentUserId = authService.getCurrentUser()?.userId;

  const chatUserId = chatUser?.userId || chatUser?.id;

  const conversationId =
    currentUserId && chatUserId
      ? [currentUserId, chatUserId].sort().join("#")
      : null;

  const [toggle, setToggle] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [messages, setMessages] = useState([]);
  const [wsStatus, setWsStatus] = useState("disconnected");

  const messagesEndRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await VivahApi.Chat.getMessages(conversationId, { limit: 10 });
      const raw = res.data?.messages || [];
      setMessages(raw.map(toMsg));
    } catch (err) {
      console.warn("[Chatting] Could not load history:", err.message);
    }
  }, [conversationId]);

  useEffect(() => {
    chatWebSocket.connect();
    const unsubStatus = chatWebSocket.onStatusChange(setWsStatus);
    const unsubMsg = chatWebSocket.onMessage((data) => {
      if (data.type === "ack") {
        const msg = toMsg(data.message);
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          const idx = prev.findIndex((m) => m.pending && m.text === msg.text && m.fromUserId === msg.fromUserId);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = msg;
            return next;
          }
          return prev;
        });
      } else if (data.type === "message") {
        const msg = toMsg(data.message);
        if (msg.conversationId === conversationId && msg.fromUserId !== currentUserId) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
        }
      }
    });
    loadHistory();
    return () => { unsubStatus(); unsubMsg(); };
  // currentUserId is stable (derived from localStorage token) — intentionally omitted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, loadHistory]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!msgText.trim() || !chatUserId) return;

    const tempId = `temp-${Date.now()}`;
    const trimmed = msgText.trim();
    setMessages((prev) => [...prev, {
      id: tempId,
      fromUserId: currentUserId,
      text: trimmed,
      timestamp: Date.now(),
      pending: true,
    }]);
    setMsgText("");

    const sent = chatWebSocket.sendMessage(chatUserId, trimmed);
    if (!sent) {
      setMessages((prev) =>
        prev.map((m) => m.id === tempId ? { ...m, pending: false, failed: true } : m)
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const avatarLetter = (chatUser?.firstName || "?")[0].toUpperCase();
  const statusClass = `chatting__dot chatting__dot--${wsStatus}`;

  return (
    <div className={`chatting${toggle ? " chatting--collapsed" : ""}`}>
      <div className="chatting__header" onClick={() => setToggle((t) => !t)}>
        <div className="chatting__header-info">
          <div className="chatting__avatar">{avatarLetter}</div>
          <div className="chatting__header-text">
            <span className="chatting__name">{chatUser?.firstName || "Chat"}</span>
            <span className="chatting__status-label">
              <span className={statusClass} />
              {wsStatus === "connected" ? "Online" : wsStatus === "connecting" ? "Connecting..." : "Offline"}
            </span>
          </div>
        </div>
        <div className="chatting__header-actions">
          <button
            className="chatting__toggle-btn"
            onClick={(e) => { e.stopPropagation(); setToggle((t) => !t); }}
            aria-label={toggle ? "Expand" : "Collapse"}
          >
            {toggle ? "▲" : "▼"}
          </button>
          {onClose && (
            <button
              className="chatting__close-btn"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              aria-label="Close chat"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {!toggle && (
        <div className="chatting__body">
          <div className="chatting__messages">
            {messages.length === 0 && (
              <div className="chatting__empty">
                <p>No messages yet. Say hello!</p>
              </div>
            )}
            {messages.map((msg, i) => {
              const isSent = msg.fromUserId === currentUserId;
              return (
                <div key={msg.id || i} className={`chatting__msg-row chatting__msg-row--${isSent ? "sent" : "received"}`}>
                  {!isSent && <div className="chatting__msg-avatar">{avatarLetter}</div>}
                  <div className={`chatting__bubble chatting__bubble--${isSent ? "sent" : "received"}`}>
                    <span className="chatting__bubble-text">{msg.text}</span>
                    <span className="chatting__bubble-meta">
                      {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                      {msg.pending && " ..."}
                      {msg.failed && " Failed"}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <footer className="chatting__footer">
            <input
              className="chatting__input"
              value={msgText}
              type="text"
              placeholder="Type a message..."
              onChange={(e) => setMsgText(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="chatting__send-btn"
              onClick={handleSend}
              disabled={!msgText.trim()}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </footer>
        </div>
      )}
    </div>
  );
}

function toMsg(msg) {
  return {
    id: msg.messageId,
    conversationId: msg.conversationId,
    fromUserId: msg.fromUserId,
    text: msg.text,
    timestamp: msg.timestamp,
  };
}

export default Chatting;
